import React, { useContext, useEffect, useRef, useState } from "react";
import { auth } from "../firebase/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import api from "services/api";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const isSigningOutDisabledUserRef = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, initializeUser);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!currentUser?.email) return;

    const intervalId = setInterval(async () => {
      try {
        await api.get(`/membersVerified/${currentUser.email}`);
      } catch (error) {
        if (error.response?.status === 403 && error.response?.data?.accountDisabled) {
          if (auth.currentUser && !isSigningOutDisabledUserRef.current) {
            isSigningOutDisabledUserRef.current = true;
            try {
              await signOut(auth);
            } catch (signOutError) {
              console.warn("Could not fully sign out disabled account session:", signOutError);
            } finally {
              isSigningOutDisabledUserRef.current = false;
            }
          }

          setCurrentUser(null);
          setUserLoggedIn(false);
        }
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [currentUser]);

  async function initializeUser(user) {
    if (!user) {
      setCurrentUser(null);
      setUserLoggedIn(false);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/membersVerified/${user.email}`);
      setCurrentUser({
        ...user,
        id: response.data.id,
        Role: response.data.Role,
        Name: response.data.Name,
        Image: response.data.Image,
        ImageThumbnail: response.data.ImageThumbnail,
      });
      setUserLoggedIn(true);
    } catch (error) {
      console.error("Auth initialization failed:", error);
      if (error.response?.status === 403 && error.response?.data?.accountDisabled) {
        if (auth.currentUser && !isSigningOutDisabledUserRef.current) {
          isSigningOutDisabledUserRef.current = true;
          try {
            await signOut(auth);
          } catch (signOutError) {
            console.warn("Could not fully sign out disabled account session:", signOutError);
          } finally {
            isSigningOutDisabledUserRef.current = false;
          }
        }
      }
      setCurrentUser(null);
      setUserLoggedIn(false);
    } finally {
      setLoading(false);
    }
  }

  const value = {
    currentUser,
    userLoggedIn,
    loading,
    setCurrentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
